<script lang="ts">
  // The detail of a task (maquettes TaskChat.dc.html and MobileTask.dc.html): who has the
  // turn, roles, status and due date (editable), documents of the task and its thread,
  // activity, and the reply zone with « Envoyer et passer la main ».
  import { api, ApiError, type Conversation, type Message, type TaskDocument, type TaskStatus } from '../lib/api';
  import { dayAndTime } from '../lib/format';
  import { render } from '../lib/markdown';
  import { app } from '../lib/stores.svelte';
  import { longDate, sinceText, statusText } from '../lib/tasks';
  import Avatar from './Avatar.svelte';
  import Composer from './Composer.svelte';
  import MessageView from './Message.svelte';

  let {
    conversation,
    root,
    replies,
    onClose,
  }: { conversation: Conversation; root: Message; replies: Message[]; onClose: () => void } = $props();

  const task = $derived(root.task!);
  const me = $derived(app.me?.id ?? '');
  const writable = $derived(conversation.is_project && !conversation.archived_at && !root.deleted);
  const members = $derived(conversation.members);
  let error = $state('');

  const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Une erreur est survenue');

  async function change(patch: Parameters<typeof api.updateTask>[1]) {
    error = '';
    try {
      app.upsertTask(await api.updateTask(root.id, patch));
    } catch (e) {
      error = message(e);
    }
  }

  async function take() {
    error = '';
    try {
      app.upsertTask(await api.takeTurn(root.id));
    } catch (e) {
      error = message(e);
    }
  }

  // --- Title -------------------------------------------------------------------------------
  let editingTitle = $state(false);
  let title = $state('');
  function startTitle() {
    title = task.title;
    editingTitle = true;
  }
  async function saveTitle(event: SubmitEvent) {
    event.preventDefault();
    if (title.trim() && title.trim() !== task.title) await change({ title });
    editingTitle = false;
  }

  // --- The turn: who handed it, when ------------------------------------------------------
  const lastTurn = $derived(
    [...replies]
      .reverse()
      .find((m) => m.kind === 'system' && ['task_handoff', 'task_take'].includes(String(m.payload?.event))),
  );
  const handedBy = $derived(
    lastTurn && lastTurn.payload?.event === 'task_handoff' && lastTurn.payload?.to === me ? lastTurn.author : null,
  );

  // --- Documents ---------------------------------------------------------------------------
  let documents = $state<TaskDocument[] | null>(null);
  $effect(() => {
    void task.document_count;
    void replies.length;
    const id = root.id;
    api
      .taskDocuments(id)
      .then((d) => id === root.id && (documents = d))
      .catch(() => (documents = []));
  });
  const previewable = (d: TaskDocument) => {
    const ext = d.name.split('.').pop()?.toLowerCase() ?? '';
    return ['text/markdown', 'text/plain', 'application/pdf'].includes(d.mime) || d.mime.startsWith('image/') || ['md', 'markdown', 'txt'].includes(ext);
  };
  function preview(d: TaskDocument) {
    app.preview = {
      ...d,
      conversation_id: root.conversation_id,
      root_id: d.message_id === root.id ? null : root.id,
    };
  }
  const ext = (name: string) => (name.includes('.') ? (name.split('.').pop() ?? '').slice(0, 4).toUpperCase() : 'FICH');

  const description = $derived(root.body ? render(root.body, app.me?.username ?? '') : '');
  const turnEvent = (m: Message) => ['task_handoff', 'task_take'].includes(String(m.payload?.event));
</script>

<aside class="panel" aria-label="Détail de la tâche">
  <header>
    <h2>Tâche <span class="where">· {conversation.title}</span></h2>
    <button type="button" class="close" aria-label="Fermer la tâche" onclick={onClose}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  </header>

  <div class="scroll">
    {#if root.deleted}
      <p class="muted">Cette tâche a été supprimée.</p>
    {:else}
      <!-- Who has the turn -->
      {#if task.status === 'done'}
        <div class="banner done">
          <span class="text"><strong>Terminée</strong>{#if task.done_at}<br />{dayAndTime(task.done_at)}{/if}</span>
          {#if writable}<button type="button" class="banner-btn" onclick={() => change({ status: 'todo' })}>Rouvrir</button>{/if}
        </div>
      {:else if task.waiting_on?.id === me}
        <div class="banner mine">
          <span class="bar" aria-hidden="true"></span>
          <span class="text">
            <strong>À vous d'agir</strong><br />
            <span class="sub">
              {#if handedBy && lastTurn}{handedBy.display_name} vous a passé la main {dayAndTime(lastTurn.created_at)}{:else}{sinceText(task.waiting_since)}{/if}
            </span>
          </span>
        </div>
      {:else if task.waiting_on}
        <div class="banner waiting">
          <Avatar user={task.waiting_on} size={28} />
          <span class="text"><strong>{task.display_status === 'waiting' ? 'En attente de' : 'La main est à'} {task.waiting_on.display_name}</strong><br />{sinceText(task.waiting_since)}</span>
          {#if writable}<button type="button" class="banner-btn" onclick={take}>Reprendre la main</button>{/if}
        </div>
      {:else}
        <div class="banner nobody">
          <span class="text"><strong>Personne n'a la main</strong><br />Choisissez un responsable, ou prenez-la.</span>
          {#if writable}<button type="button" class="banner-btn" onclick={take}>Prendre la main</button>{/if}
        </div>
      {/if}

      {#if editingTitle}
        <form class="title-form" onsubmit={saveTitle}>
          <label class="sr-only" for="task-title">Intitulé</label>
          <input id="task-title" class="input" maxlength="200" bind:value={title} />
          <button type="submit" class="btn primary">Enregistrer</button>
          <button type="button" class="btn" onclick={() => (editingTitle = false)}>Annuler</button>
        </form>
      {:else}
        <h3 class:done={task.status === 'done'}>
          {task.title}
          {#if writable}<button type="button" class="edit" aria-label="Modifier l'intitulé" onclick={startTitle}>
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L19 9l-4-4L4 16zM14 6l4 4" /></svg>
            </button>{/if}
        </h3>
      {/if}

      <dl>
        <dt><label for="task-requester">Demandeur</label></dt>
        <dd>
          <Avatar user={task.requester} size={24} />
          <select id="task-requester" disabled={!writable} value={task.requester.id} onchange={(e) => change({ requester_id: e.currentTarget.value })}>
            {#each members as m (m.id)}<option value={m.id}>{m.display_name}{m.id === me ? ' (vous)' : ''}</option>{/each}
          </select>
        </dd>
        <dt><label for="task-assignee">Responsable</label></dt>
        <dd>
          {#if task.assignee}<Avatar user={task.assignee} size={24} />{/if}
          <select id="task-assignee" disabled={!writable} value={task.assignee?.id ?? ''} onchange={(e) => change({ assignee_id: e.currentTarget.value || null })}>
            <option value="">Non attribuée</option>
            {#each members as m (m.id)}<option value={m.id}>{m.display_name}{m.id === me ? ' (vous)' : ''}</option>{/each}
          </select>
        </dd>
        <dt><label for="task-status">Statut</label></dt>
        <dd>
          <span class="pill {task.display_status}">{statusText(task)}</span>
          <select id="task-status" class="compact" aria-label="Changer le statut" disabled={!writable} value={task.status} onchange={(e) => change({ status: e.currentTarget.value as TaskStatus })}>
            <option value="todo">À faire</option>
            <option value="doing">En cours</option>
            <option value="done">Terminé</option>
          </select>
        </dd>
        <dt><label for="task-due">Échéance</label></dt>
        <dd>
          <input
            id="task-due"
            type="date"
            title={task.due_date ? longDate(task.due_date) : 'Sans échéance'}
            disabled={!writable}
            value={task.due_date ?? ''}
            onchange={(e) => change({ due_date: e.currentTarget.value || null })}
          />
        </dd>
      </dl>
      {#if error}<p class="error" role="alert">{error}</p>{/if}

      {#if description}<div class="description">{@html description}</div>{/if}

      <section class="section" aria-labelledby="docs-title">
        <div class="section-title"><h4 id="docs-title">Documents</h4><span class="n">{documents?.length ?? task.document_count}</span><span class="rule"></span></div>
        {#if documents && documents.length === 0}
          <p class="muted small">Aucun document. Joignez-en un au commentaire, ou au passage de main.</p>
        {/if}
        {#each documents ?? [] as d (d.id)}
          <div class="doc">
            <span class="ext" aria-hidden="true">{ext(d.name)}</span>
            <span class="doc-text">
              <span class="doc-name">{d.name}</span>
              <span class="doc-meta" class:handoff={d.handoff}>
                {d.author.display_name} · {dayAndTime(d.created_at)} · {d.message_id === root.id ? 'demande initiale' : d.handoff ? 'passage de main' : 'commentaire'}
              </span>
            </span>
            {#if previewable(d)}<button type="button" class="link" onclick={() => preview(d)}>Aperçu</button>{/if}
          </div>
        {/each}
      </section>

      <section class="section" aria-label="Activité">
        <div class="section-title"><h4>Activité</h4><span class="rule"></span></div>
        <p class="line">
          {root.author.display_name} a créé la tâche{task.assignee ? ` pour ${task.assignee.display_name}` : ''} · {dayAndTime(root.created_at)}
        </p>
        {#each replies as r (r.id)}
          {#if r.kind === 'system'}
            <p class="line" class:turn={turnEvent(r)}>
              {#if turnEvent(r)}<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>{/if}
              {r.body} · {dayAndTime(r.created_at)}
            </p>
          {:else}
            <MessageView message={r} inThread archived={!!conversation.archived_at} />
          {/if}
        {/each}
      </section>
    {/if}
  </div>

  {#if !root.deleted && !conversation.archived_at}
    {#key root.id}
      <Composer
        conversationId={conversation.id}
        rootId={root.id}
        handoffFor={writable && task.status !== 'done' ? root : null}
        placeholder="Commenter la tâche"
      />
    {/key}
  {/if}
</aside>

<style>
  .panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    border-left: 1px solid var(--border);
    min-width: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: var(--header-h);
    padding: 8px 12px 8px 20px;
    border-bottom: 1px solid var(--border);
  }
  h2 {
    flex: 1;
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .where {
    color: var(--muted);
    font-weight: 400;
    font-size: 14px;
  }
  .close {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 20px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .banner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 18px;
  }
  .banner .text {
    flex: 1 1 160px;
  }
  .banner.mine {
    background: var(--navy);
    color: #ffffff;
    font-size: 14px;
  }
  .banner.mine .bar {
    flex: 0 0 6px;
    align-self: stretch;
    border-radius: 3px;
    background: var(--accent);
  }
  .banner.mine .sub {
    opacity: 0.85;
    font-size: 13px;
  }
  .banner.waiting {
    background: var(--status-waiting-soft);
    color: var(--status-waiting-text);
  }
  .banner.done {
    background: var(--status-done-soft);
    color: var(--status-done);
  }
  .banner.nobody {
    background: var(--status-todo-soft);
    color: var(--status-todo-text);
  }
  .banner-btn {
    height: 32px;
    padding: 0 10px;
    border: 1px solid currentColor;
    border-radius: 8px;
    background: var(--bg);
    color: inherit;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }
  h3 {
    margin: 0;
    font-size: 18px;
    line-height: 26px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  h3.done {
    color: var(--muted);
  }
  .edit {
    border: 0;
    background: none;
    color: var(--muted);
    padding: 2px 4px;
    vertical-align: middle;
  }
  .title-form {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .title-form .input {
    flex: 1 1 200px;
  }
  dl {
    margin: 0;
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 10px 12px;
    align-items: center;
  }
  dt {
    color: var(--muted);
    font-size: 13px;
  }
  dd {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  input[type='date'] {
    min-width: 150px;
  }
  select,
  input[type='date'] {
    max-width: 100%;
    height: 30px;
    padding: 0 6px;
    border: 1px solid transparent;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--ink);
  }
  select:hover:not(:disabled),
  input[type='date']:hover:not(:disabled),
  select:focus,
  input[type='date']:focus {
    border-color: var(--border-strong);
    background: var(--bg);
  }
  select.compact {
    width: 32px;
    color: var(--muted);
  }
  .description {
    overflow-wrap: anywhere;
    color: var(--ink-2);
  }
  .description :global(p) {
    margin: 0 0 6px;
  }
  .section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .section-title h4 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .n {
    font-size: 12px;
    color: var(--muted);
  }
  .rule {
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  .doc {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
  }
  .ext {
    flex: 0 0 32px;
    height: 32px;
    border-radius: 6px;
    background: var(--accent-soft);
    color: var(--accent-on-soft);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .doc-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .doc-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .doc-meta {
    font-size: 12px;
    color: var(--muted);
  }
  .doc-meta.handoff {
    color: var(--status-waiting-text);
    font-weight: 500;
  }
  .link {
    border: 0;
    background: none;
    color: var(--accent-text);
    font-size: 13px;
    font-weight: 600;
    padding: 0 4px;
  }
  .line {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Comments of the activity: narrower than a conversation. */
  .section :global(.message) {
    padding-left: 0;
    padding-right: 0;
  }
  .section :global(.files),
  .section :global(.file) {
    max-width: 100%;
  }
  .section :global(.file-name) {
    min-width: 0;
  }
  .section :global(.file-line) {
    flex-wrap: wrap;
  }
  .line.turn {
    color: var(--status-waiting-text);
    font-weight: 500;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
</style>
